import { useMemo } from "react";
import { Link } from "wouter";
import { Play, ArrowRight, Boxes, Loader2 } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  useGames,
  useLeaderboardByRange,
  useMasterLeaderboard,
  useMe,
  useMyActiveGames,
  useRoster,
  useStatsPending,
} from "@/lib/queries";
import { useStickyState } from "@/lib/useStickyState";
import type { LeaderboardRange } from "@/lib/types";
import { ActiveGamesBanner } from "@/components/ActiveGamesBanner";

// ---- Sticky tab option types

type RecentTab = "live" | "virtual";
type RangeTab = LeaderboardRange;

const RANGE_OPTIONS: { id: RangeTab; label: string }[] = [
  { id: "1d", label: "Today" },
  { id: "7d", label: "7d" },
  { id: "30d", label: "30d" },
  { id: "90d", label: "90d" },
  { id: "all", label: "All" },
];

export default function Dashboard() {
  const { data: me } = useMe();
  const { data: games = [] } = useGames();
  // Still call the master leaderboard for the "Players" count tile
  // (cheap, all-time, used only as a global counter). The home-page
  // top-of-the-board reads from the server-cached by-range endpoint
  // below — that's the volume-ready path (queue → worker → cache).
  const { data: leaderboardAll = [] } = useMasterLeaderboard();

  // Sticky preferences. Both default to the most common selection
  // ("Live" games + "30d" range) so a returning user lands on
  // familiar data, then can swap tabs without losing the selection
  // on next visit.
  const [recentTab, setRecentTab] = useStickyState<RecentTab>(
    "dashboard.recentTab",
    "live",
  );
  const [rangeTab, setRangeTab] = useStickyState<RangeTab>(
    "dashboard.rangeTab",
    "30d",
  );

  // Server-cached, queue-refreshed leaderboard for the selected
  // range. The API stacks three layers: in-process LRU → DB cache
  // row → SQL aggregation. Game completions enqueue a stats job
  // that recomputes + primes the cache so the next hit is hot.
  const { data: leaderboardPayload, isPending: leaderboardPending } =
    useLeaderboardByRange(rangeTab);
  const leaderboard = useMemo(
    () => (leaderboardPayload?.rows ?? []).slice(0, 5),
    [leaderboardPayload],
  );

  // Pending-job poller. While the worker is behind, this returns a
  // positive count and we show an "Updating stats…" badge. Stops
  // polling automatically when the queue drains (refetchInterval
  // returns false on zero — see the hook).
  const { data: pendingStats } = useStatsPending();
  const isUpdatingStats = (pendingStats?.pending ?? 0) > 0;

  // Active games where this user is on the roster (live or
  // scheduled). Surfaces a "Resume" banner above the fold so
  // returning to a mid-play game is one tap — and a freshly
  // created invite from a friend shows up here within ~15s
  // even if the push notification is missed or dismissed.
  //
  // Cross-org: the endpoint queries by user.id so it walks every
  // org membership the user has. The roster lookup is kept only
  // for the banner row's "is this me?" check.
  const { data: roster = [] } = useRoster();
  const myPlayer = useMemo(
    () => roster.find((p) => p.userId === me?.id) ?? null,
    [roster, me],
  );
  const { data: activeGames = [] } = useMyActiveGames(me?.id ?? null);

  // Filter the recent-games list by tab. "Live" = real-life games
  // (isVirtual false/null); "Virtual" = the 3D mode.
  const filteredRecent = useMemo(
    () =>
      games.filter((g) =>
        recentTab === "virtual" ? g.isVirtual : !g.isVirtual,
      ),
    [games, recentTab],
  );

  return (
    <div className="px-4 sm:px-8 py-6 sm:py-10 max-w-6xl mx-auto space-y-6">
      <header className="flex flex-col sm:flex-row sm:items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-display uppercase tracking-tight">
            Welcome{me?.displayName ? `, ${me.displayName.split(" ")[0]}` : ""}
          </h1>
          <p className="text-muted-foreground text-sm">
            What's happening on the boards.
          </p>
        </div>
        <Button asChild size="lg" className="hidden sm:inline-flex">
          <Link href="/dashboard/play">
            <Play size={18} />
            Play
          </Link>
        </Button>
      </header>

      {/* Above-the-fold "you have a live game" prompt. Hidden when
          empty so it doesn't take up space on a quiet dashboard.
          Polls every 15s so a friend's freshly-created invite
          surfaces here even without (or in addition to) the
          push notification. */}
      <ActiveGamesBanner
        games={activeGames}
        myPlayerId={myPlayer?.id ?? null}
      />

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Stat label="Games" value={games.length} />
        <Stat
          label="Completed"
          value={games.filter((g) => g.status === "complete").length}
        />
        <Stat label="Live" value={games.filter((g) => g.status === "live").length} />
        <Stat label="Players" value={leaderboardAll.length} />
      </div>

      {/* Tiny inline link surfaces the org-wide insights page
          (weekly activity, format mix, round distribution,
          notable games). Keeps the discoverable surface low-key
          rather than competing with the two main cards below. */}
      <div className="flex justify-end">
        <Link
          href="/dashboard/insights"
          className="text-xs text-primary font-medium hover:underline inline-flex items-center gap-1"
        >
          View insights <ArrowRight size={12} />
        </Link>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader className="space-y-3">
            <div className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Recent games</CardTitle>
                <CardDescription>
                  {recentTab === "virtual"
                    ? "Latest 3D scenes from your org."
                    : "Latest backyard scores from your org."}
                </CardDescription>
              </div>
              <Link
                href={recentTab === "virtual" ? "/dashboard/virtual" : "/dashboard/games"}
                className="text-sm text-primary font-medium hover:underline flex items-center gap-1"
              >
                All <ArrowRight size={14} />
              </Link>
            </div>
            {/* Sticky tabs: persist the user's Live/Virtual preference
                between visits via useStickyState so a player who
                lives in Virtual mode doesn't have to re-tap every
                page load. */}
            <TabStrip
              options={[
                { id: "live", label: "Live", icon: <Play size={12} /> },
                { id: "virtual", label: "Virtual", icon: <Boxes size={12} /> },
              ]}
              value={recentTab}
              onChange={setRecentTab}
            />
          </CardHeader>
          <CardContent className="p-0">
            {filteredRecent.length === 0 ? (
              <Empty
                hint={
                  recentTab === "virtual"
                    ? "No virtual games yet. Spin one up from Virtual."
                    : "No live games yet. Log your first."
                }
              />
            ) : (
              <ul className="divide-y divide-border">
                {filteredRecent.slice(0, 5).map((g) => (
                  <li key={g.id}>
                    <Link
                      href={`/dashboard/games/${g.id}`}
                      className="flex items-center gap-3 p-3 hover:bg-muted/40"
                    >
                      <div className="shrink-0 w-12 text-xs text-muted-foreground">
                        {new Date(g.playedOn).toLocaleDateString(undefined, {
                          month: "short",
                          day: "numeric",
                        })}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div
                          className={`text-sm truncate ${
                            g.winningTeam === 1
                              ? "font-semibold text-foreground"
                              : "text-muted-foreground"
                          }`}
                        >
                          {g.team1Players.map((p) => p.name).join(" & ")}
                        </div>
                        <div
                          className={`text-sm truncate ${
                            g.winningTeam === 2
                              ? "font-semibold text-foreground"
                              : "text-muted-foreground"
                          }`}
                        >
                          {g.team2Players.map((p) => p.name).join(" & ")}
                        </div>
                      </div>
                      <div className="shrink-0 grid grid-rows-2 text-right font-display tabular-nums text-sm leading-tight px-2 py-1 rounded border border-border bg-background min-w-[3rem]">
                        <span
                          className={
                            g.winningTeam === 1
                              ? "text-primary font-semibold"
                              : "text-muted-foreground"
                          }
                        >
                          {g.team1Score}
                        </span>
                        <span
                          className={
                            g.winningTeam === 2
                              ? "text-primary font-semibold"
                              : "text-muted-foreground"
                          }
                        >
                          {g.team2Score}
                        </span>
                      </div>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="space-y-3">
            <div className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  Top of the board
                  {/* Subtle pill that flashes while the stats worker
                      is behind — disappears the moment the queue
                      drains so users know data is fresh. */}
                  {isUpdatingStats ? (
                    <span className="inline-flex items-center gap-1 text-[10px] font-display uppercase tracking-wider text-muted-foreground bg-muted/40 border border-border px-1.5 py-0.5 rounded">
                      <Loader2 size={10} className="animate-spin" />
                      Updating
                    </span>
                  ) : null}
                </CardTitle>
                <CardDescription>
                  Rating{" "}
                  {rangeTab === "all"
                    ? "all time"
                    : rangeTab === "1d"
                      ? "today"
                      : `over the last ${rangeTab.replace("d", " days")}`}
                  .
                </CardDescription>
              </div>
              <Link
                href="/dashboard/leaderboards"
                className="text-sm text-primary font-medium hover:underline flex items-center gap-1"
              >
                Full <ArrowRight size={14} />
              </Link>
            </div>
            {/* Date range tabs: sticky so a returning user sees the
                same window. Backend-cached, queue-refreshed — see
                useLeaderboardByRange. */}
            <TabStrip
              options={RANGE_OPTIONS.map((r) => ({ id: r.id, label: r.label }))}
              value={rangeTab}
              onChange={setRangeTab}
            />
          </CardHeader>
          <CardContent className="p-0">
            {leaderboardPending ? (
              <Empty hint="Loading…" />
            ) : leaderboardPayload?.computing ? (
              // Cold cache miss: the worker is warming this org's
              // ranks. Distinct from the genuinely-empty state so we
              // don't tell a returning org to "play a few games".
              <Empty hint="Crunching the numbers…" />
            ) : leaderboard.length === 0 ? (
              <Empty
                hint={
                  rangeTab === "all"
                    ? "Play a few games to start the rankings."
                    : rangeTab === "1d"
                      ? "No games today yet — play one to lead the day!"
                      : `No completed games in the last ${rangeTab.replace("d", " days")}. Try "All".`
                }
              />
            ) : (
              <ul className="divide-y divide-border">
                {leaderboard.map((row, idx) => {
                  const slug =
                    row.slug ??
                    roster.find((p) => p.id === row.playerId)?.slug;
                  const inner = (
                    <>
                      <div className="w-6 text-center font-display text-lg shrink-0">
                        {idx + 1}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium truncate">{row.name}</div>
                        <div className="text-[11px] text-muted-foreground tabular-nums">
                          {row.wins}W · {row.losses}L ·{" "}
                          {(row.winPct * 100).toFixed(0)}% win
                        </div>
                        {/* Per-row rating bar — a visual at-a-glance read of
                            each player's standing vs the date range. */}
                        <div
                          className="mt-1.5 h-1.5 rounded-full bg-muted/30 overflow-hidden"
                          aria-label={`rating ${(row.rating ?? 0).toFixed(0)}`}
                        >
                          <div
                            className="h-full bg-primary"
                            style={{ width: `${Math.min(100, row.rating ?? 0)}%` }}
                          />
                        </div>
                      </div>
                      <div className="text-right tabular-nums shrink-0 min-w-[3rem]">
                        <div className="font-display text-lg leading-none">
                          {(row.rating ?? 0).toFixed(0)}
                        </div>
                        <div className="text-[10px] text-muted-foreground uppercase tracking-wider mt-0.5">
                          Rating
                        </div>
                      </div>
                    </>
                  );
                  return (
                    <li key={row.playerId}>
                      {slug ? (
                        <Link
                          href={`/dashboard/players/${slug}`}
                          className="flex items-center gap-3 p-3 hover:bg-muted/40 transition-colors"
                        >
                          {inner}
                        </Link>
                      ) : (
                        <div className="flex items-center gap-3 p-3">{inner}</div>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

// Small inline tab strip shared by the Recent / Range selectors.
// Generic over the option id so each consumer keeps its own union
// type (RecentTab vs RangeTab). Tabs render in horizontal order;
// the active one gets primary coloring + a subtle ring.
function TabStrip<T extends string>({
  options,
  value,
  onChange,
}: {
  options: Array<{ id: T; label: string; icon?: React.ReactNode }>;
  value: T;
  onChange: (next: T) => void;
}) {
  return (
    <div className="inline-flex items-center gap-1 rounded-md border border-border bg-muted/20 p-0.5 text-xs">
      {options.map((opt) => {
        const active = opt.id === value;
        return (
          <button
            key={opt.id}
            type="button"
            onClick={() => onChange(opt.id)}
            className={
              "inline-flex items-center gap-1 px-2.5 py-1 rounded font-display uppercase tracking-wider transition-colors " +
              (active
                ? "bg-primary text-primary-foreground"
                : "text-muted-foreground hover:text-foreground")
            }
            aria-pressed={active}
          >
            {opt.icon}
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <Card>
      <CardContent className="p-4">
        <div className="text-[10px] font-display uppercase tracking-wider text-muted-foreground">
          {label}
        </div>
        <div className="text-3xl font-display tabular-nums mt-1">{value}</div>
      </CardContent>
    </Card>
  );
}

function Empty({ hint }: { hint: string }) {
  return <div className="p-8 text-center text-muted-foreground text-sm">{hint}</div>;
}
